// src/pages/ResetPassword.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  IconButton as MIconButton,
  Button,
  Divider,
  Alert,
  Snackbar,
  LinearProgress,
  Link as MUILink,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { authService } from "../services/resources.service";

async function requestPasswordReset(email) {
  return authService.requestReset(email);
}

async function confirmPasswordReset({ token, password }) {
  return authService.confirmReset(token, password);
}

// ---- Helpers ----
function passwordScore(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}
function strengthLabel(score) {
  return ["Very weak", "Weak", "Okay", "Good", "Strong"][score] || "Weak";
}

export default function ResetPassword() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const token = qs.get("token") || qs.get("oobCode") || "";

  // Mode: request email link OR set new password
  const isResetMode = Boolean(token);

  // Request form
  const [email, setEmail] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);

  // Reset form
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  const pwScore = useMemo(() => passwordScore(password), [password]);
  const passwordsMatch = confirm.length === 0 || password === confirm;

  const canSendEmail = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !requestLoading,
    [email, requestLoading]
  );
  const canReset = useMemo(() => {
    if (resetLoading) return false;
    if (!password || password.length < 8) return false;
    if (!passwordsMatch) return false;
    return true;
  }, [resetLoading, password, passwordsMatch]);

  const doRequest = async (e) => {
    e.preventDefault();
    if (!canSendEmail) return;
    setRequestLoading(true);
    setRequestError("");
    try {
      await requestPasswordReset(email.trim());
      setRequestSuccess(true);
      setSnack({ open: true, msg: "Reset link sent (check your email).", severity: "success" });
    } catch (err) {
      setRequestError(err.message || "Failed to send reset link.");
    } finally {
      setRequestLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!canReset) return;
    setResetLoading(true);
    setResetError("");
    try {
      await confirmPasswordReset({ token, password });
      setResetSuccess(true);
      setSnack({ open: true, msg: "Password updated. You can now sign in.", severity: "success" });
    } catch (err) {
      setResetError(err.message || "Failed to reset password.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            {isResetMode ? "Set new password" : "Reset password"}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "grid", placeItems: "center", p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 520,
            p: 2,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
          }}
        >
          {/* REQUEST LINK MODE */}
          {!isResetMode && (
            <Stack spacing={2}>
              <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Band-Joe logo"
                  sx={{ width: 88, height: 88, objectFit: "contain", mb: 0.5 }}
                />
                <Typography variant="h5" fontWeight={900}>
                  Forgot your password?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter your email and we’ll send you a reset link.
                </Typography>
              </Stack>

              <Box component="form" onSubmit={doRequest}>
                <Stack spacing={1.25}>
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {!!requestError && <Alert severity="error">{requestError}</Alert>}
                  {requestSuccess && (
                    <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                      If an account exists for <b>{email}</b>, you’ll receive an email shortly.
                    </Alert>
                  )}
                  <Button type="submit" variant="contained" size="large" disabled={!canSendEmail} fullWidth>
                    {requestLoading ? "Sending…" : "Send reset link"}
                  </Button>
                </Stack>
              </Box>

              <Divider />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Remembered it?{" "}
                <MUILink component={Link} to="/auth" underline="hover">
                  Back to sign in
                </MUILink>
              </Typography>
            </Stack>
          )}

          {/* SET NEW PASSWORD MODE */}
          {isResetMode && (
            <Stack spacing={2}>
              <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Band-Joe logo"
                  sx={{ width: 88, height: 88, objectFit: "contain", mb: 0.5 }}
                />
                <Typography variant="h5" fontWeight={900}>
                  Create a new password
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter and confirm your new password to finish resetting.
                </Typography>
              </Stack>

              <Box component="form" onSubmit={doReset}>
                <Stack spacing={1.25}>
                  <TextField
                    label="New password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    helperText="Use at least 8 characters with a mix of letters, numbers & symbols."
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <MIconButton onClick={() => setShowPw((s) => !s)} edge="end" aria-label="toggle password visibility">
                            {showPw ? <VisibilityOff /> : <Visibility />}
                          </MIconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box>
                    <LinearProgress variant="determinate" value={(passwordScore(password) / 4) * 100} sx={{ borderRadius: 999, height: 8 }} />
                    <Typography variant="caption" color="text.secondary">
                      Strength: {strengthLabel(passwordScore(password))}
                    </Typography>
                  </Box>

                  <TextField
                    label="Confirm password"
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    error={confirm.length > 0 && !passwordsMatch}
                    helperText={confirm.length > 0 && !passwordsMatch ? "Passwords do not match" : " "}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <MIconButton onClick={() => setShowConfirm((s) => !s)} edge="end" aria-label="toggle confirm visibility">
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </MIconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {!!resetError && <Alert severity="error">{resetError}</Alert>}
                  {resetSuccess && (
                    <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success">
                      Password updated.{" "}
                      <MUILink component={Link} to="/auth" underline="hover">
                        Sign in
                      </MUILink>
                      .
                    </Alert>
                  )}

                  <Button type="submit" variant="contained" size="large" disabled={!canReset} fullWidth>
                    {resetLoading ? "Updating…" : "Update password"}
                  </Button>
                </Stack>
              </Box>

              <Divider />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Wrong page?{" "}
                <MUILink component={Link} to="/auth" underline="hover">
                  Back to sign in
                </MUILink>
              </Typography>
            </Stack>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
